import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { User, Trash2, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import './JobApplicationsPage.scss';

interface JobApplication {
  id: number;
  job_id: number;
  user_id: number;
  cv_id: number | null;
  cover_letter_id: number | null;
  additional_message: string | null;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected' | 'hired';
  applied_at: string;
  reviewed_at: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  profile_photo: string | null;
  age?: number;
  city?: string;
  current_position?: string;
  company_name?: string;
  work_experience?: string;
  desired_salary?: string;
  desired_position?: string;
  job_title: string;
}

interface InvitedCandidate {
  id: number;
  candidate_id: number;
  cv_id: number | null;
  status: string;
  employer_evaluation: 'pending' | 'reviewed' | 'accepted' | 'rejected' | 'hired';
  sent_at: string;
  candidate_name: string;
  candidate_email?: string;
  candidate_phone?: string;
  candidate_photo?: string;
  job_title?: string;
}

type FilterTab = 'all' | 'pending' | 'accepted' | 'rejected' | 'hired';

const FILTER_LABELS: Record<FilterTab, string> = {
  all: 'Visi',
  pending: 'Nauji',
  accepted: 'Tinkamas',
  rejected: 'Netinkamas',
  hired: 'Priimtas',
};

const EVAL_OPTIONS = [
  { value: 'pending', label: 'Naujas' },
  { value: 'reviewed', label: 'Peržiūrėta' },
  { value: 'accepted', label: 'Tinkamas' },
  { value: 'rejected', label: 'Netinkamas' },
  { value: 'hired', label: 'Priimtas' },
];

const JobApplicationsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [invitations, setInvitations] = useState<InvitedCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobTitle, setJobTitle] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<JobApplication | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const { showToast } = useToast();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');

      // Fetch applications
      const appResponse = await fetch(`${API_BASE_URL}/job-applications/job/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (appResponse.ok) {
        const appResult = await appResponse.json();
        setApplications(appResult.data || []);
        if (appResult.data?.length > 0) {
          setJobTitle(appResult.data[0].job_title);
        }
      } else {
        const errorData = await appResponse.json();
        setError(errorData.message || 'Nepavyko užkrauti prašymų');
      }

      // Fetch invited candidates
      try {
        const invResponse = await fetch(`${API_BASE_URL}/job-invitations/job/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (invResponse.ok) {
          const invResult = await invResponse.json();
          const invData = invResult.data || [];
          setInvitations(invData);
          if (!jobTitle && invData.length > 0 && invData[0].job_title) {
            setJobTitle(invData[0].job_title);
          }
        }
      } catch {
        // non-critical
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Įvyko klaida užkraunant duomenis');
    } finally {
      setLoading(false);
    }
  }, [jobId, API_BASE_URL, jobTitle]);

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      navigate('/');
      return;
    }
    loadData();
  }, [jobId, user, navigate, loadData]);

  const handleAppStatusChange = async (applicationId: number, newStatus: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/job-applications/${applicationId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setApplications(prev => prev.map(app =>
          app.id === applicationId ? { ...app, status: newStatus as any } : app
        ));
      } else {
        showToast('Nepavyko atnaujinti būsenos', 'error');
      }
    } catch {
      showToast('Įvyko klaida atnaujinant būseną', 'error');
    }
  };

  const handleInvEvalChange = async (invitationId: number, evaluation: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/job-invitations/${invitationId}/evaluation`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ evaluation })
      });

      if (response.ok) {
        setInvitations(prev => prev.map(inv =>
          inv.id === invitationId ? { ...inv, employer_evaluation: evaluation as any } : inv
        ));
      } else {
        showToast('Nepavyko atnaujinti įvertinimo', 'error');
      }
    } catch {
      showToast('Įvyko klaida atnaujinant įvertinimą', 'error');
    }
  };

  const handleDeleteApplication = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/job-applications/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setApplications(prev => prev.filter(a => a.id !== deleteTarget.id));
      }
    } catch (err) {
      console.error('Error deleting application:', err);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleViewCV = (application: JobApplication) => {
    if (application.cv_id) {
      const returnPath = `/jobs/${jobId}/applications?mode=employer`;
      navigate(`/cv/${application.cv_id}?returnUrl=${encodeURIComponent(returnPath)}`);
    } else {
      showToast('Kandidatas nepateikė CV', 'info');
    }
  };

  const handleViewCoverLetter = (message: string | null) => {
    if (message) {
      const modal = document.createElement('div');
      modal.className = 'cover-letter-modal';
      modal.innerHTML = `
        <div class="modal-overlay" onclick="this.closest('.cover-letter-modal').remove()">
          <div class="modal-content" onclick="event.stopPropagation()">
            <div class="modal-header">
              <h3>Motyvacinis laiškas</h3>
              <button class="modal-close" onclick="this.closest('.cover-letter-modal').remove()">×</button>
            </div>
            <div class="modal-body"><p>${message.replace(/\n/g, '<br>')}</p></div>
            <div class="modal-footer">
              <button class="btn-close" onclick="this.closest('.cover-letter-modal').remove()">Uždaryti</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
    } else {
      showToast('Kandidatas nepateikė motyvacinio laiško', 'info');
    }
  };

  const getProfilePhotoUrl = (photo: string | null, name?: string): string => {
    if (!photo) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=23928B&color=fff&size=200`;
    }
    if (photo.startsWith('http')) return photo;
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}${photo.startsWith('/') ? photo : `/${photo}`}`;
  };

  const formatDateLong = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  // Filtering logic: filter both lists by the active tab
  const filteredApps = activeFilter === 'all'
    ? applications
    : applications.filter(a => a.status === activeFilter);

  const filteredInvs = activeFilter === 'all'
    ? invitations
    : invitations.filter(i => (i.employer_evaluation || 'pending') === activeFilter);

  // Count per status across both lists
  const statusCounts = (status: FilterTab): number => {
    if (status === 'all') return applications.length + invitations.length;
    const appCount = applications.filter(a => a.status === status).length;
    const invCount = invitations.filter(i => (i.employer_evaluation || 'pending') === status).length;
    return appCount + invCount;
  };

  if (loading) {
    return (
      <div className="job-applications-page">
        <div className="container">
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Kraunami kandidatai...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAny = applications.length > 0 || invitations.length > 0;

  return (
    <div className="job-applications-page">
      <div className="container">
        <div className="breadcrumbs">
          <Link to="/">Titulinis</Link>
          <ChevronRight size={14} />
          <Link to="/jobs?mode=employer">Skelbimai</Link>
          <ChevronRight size={14} />
          <span>{jobTitle || 'Kandidatai'}</span>
        </div>

        {error && <div className="alert alert--error">{error}</div>}

        {!hasAny ? (
          <div className="empty-state">
            <User size={48} />
            <h3>Kandidatų nerasta</h3>
            <p>Šiam darbo skelbimui dar niekas nėra pateikęs prašymo.</p>
          </div>
        ) : (
          <>
            {/* Filter tabs */}
            <div className="filter-tabs">
              {(Object.keys(FILTER_LABELS) as FilterTab[]).map(tab => {
                const count = statusCounts(tab);
                return (
                  <button
                    key={tab}
                    className={`filter-tab ${activeFilter === tab ? 'active' : ''}`}
                    onClick={() => setActiveFilter(tab)}
                  >
                    {FILTER_LABELS[tab]}
                    {count > 0 && <span className="filter-tab__count">{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Applications section */}
            {filteredApps.length > 0 && (
              <div className="candidates-section">
                <h3 className="section-heading">Darbuotojų aplikacijos ({filteredApps.length})</h3>
                <div className="candidates-grid">
                  {filteredApps.map((app) => (
                    <div key={`app-${app.id}`} className="candidate-card">
                      <div className="candidate-card__header candidate-card__header--app">
                        <button
                          className="icon-btn icon-btn--danger"
                          title="Ištrinti"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(app); }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      <div className="candidate-card__photo-container">
                        <div className="candidate-card__photo">
                          <img
                            src={getProfilePhotoUrl(app.profile_photo, `${app.first_name} ${app.last_name}`)}
                            alt={`${app.first_name} ${app.last_name}`}
                            onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${app.first_name}+${app.last_name}&background=23928B&color=fff&size=200`; }}
                          />
                        </div>
                      </div>
                      <h3 className="candidate-card__name">
                        {app.first_name} {app.last_name} {app.age ? `${app.age} m.` : ''} {app.city}
                      </h3>

                      {(app.company_name || app.current_position || app.work_experience) && (
                        <div className="candidate-card__section">
                          <p className="section-title">Darbo patirtis (paskutinė pozicija)</p>
                          <p className="section-content">
                            {app.company_name && `${app.company_name}, `}{app.current_position}
                            {app.work_experience && ` (${app.work_experience})`}
                          </p>
                        </div>
                      )}

                      {app.desired_position && (
                        <div className="candidate-card__section">
                          <p className="section-title">Ieškomas darbas</p>
                          <p className="section-content">{app.desired_position}</p>
                        </div>
                      )}

                      {app.desired_salary && (
                        <div className="candidate-card__section">
                          <p className="section-title">Pageidaujamas atlyginimas:</p>
                          <p className="section-content salary">{app.desired_salary}</p>
                        </div>
                      )}

                      <div className="candidate-card__activity">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
                        <span className="activity-text">Kandidatavo {formatDateLong(app.applied_at)}</span>
                      </div>

                      <div className="candidate-card__evaluation">
                        <label>Įvertinkite kandidatą:</label>
                        <select
                          value={app.status}
                          onChange={(e) => handleAppStatusChange(app.id, e.target.value)}
                          className="evaluation-select"
                        >
                          {EVAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>

                      {app.additional_message && (
                        <div className="candidate-card__letter">
                          <label>Motyvacinis laiškas:</label>
                          <button className="btn-outline" onClick={() => handleViewCoverLetter(app.additional_message)}>ŽIŪRĖTI</button>
                        </div>
                      )}

                      <button className="btn btn--view-cv" onClick={() => handleViewCV(app)} disabled={!app.cv_id}>
                        ŽIŪRĖTI CV
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invited candidates section */}
            {filteredInvs.length > 0 && (
              <div className="candidates-section candidates-section--invited">
                <h3 className="section-heading section-heading--invited">Pakviesti kandidatai ({filteredInvs.length})</h3>
                <div className="candidates-grid">
                  {filteredInvs.map((inv) => (
                    <div key={`inv-${inv.id}`} className="candidate-card">
                      <div className="candidate-card__header candidate-card__header--invited">
                        <span className="candidate-card__badge">Pakviestas</span>
                      </div>
                      <div className="candidate-card__photo-container">
                        <div className="candidate-card__photo">
                          <img
                            src={getProfilePhotoUrl(inv.candidate_photo || null, inv.candidate_name)}
                            alt={inv.candidate_name}
                            onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(inv.candidate_name || 'K')}&background=23928B&color=fff&size=200`; }}
                          />
                        </div>
                      </div>
                      <h3 className="candidate-card__name">{inv.candidate_name}</h3>
                      {inv.candidate_email && <p className="candidate-card__email">{inv.candidate_email}</p>}

                      <div className="candidate-card__activity">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
                        <span className="activity-text">Pakviesta {formatDateLong(inv.sent_at)}</span>
                      </div>

                      <div className="candidate-card__evaluation">
                        <label>Įvertinkite kandidatą:</label>
                        <select
                          value={inv.employer_evaluation || 'pending'}
                          onChange={(e) => handleInvEvalChange(inv.id, e.target.value)}
                          className="evaluation-select"
                        >
                          {EVAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>

                      <button
                        className="btn btn--view-cv"
                        onClick={() => {
                          const returnPath = encodeURIComponent(`/jobs/${jobId}/applications?mode=employer`);
                          if (inv.cv_id) {
                            navigate(`/cv/${inv.cv_id}?returnUrl=${returnPath}`);
                          } else {
                            navigate(`/candidate/${inv.candidate_id}?mode=employer&returnUrl=${returnPath}`);
                          }
                        }}
                      >
                        ŽIŪRĖTI CV
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredApps.length === 0 && filteredInvs.length === 0 && (
              <div className="empty-state">
                <User size={48} />
                <h3>Nėra kandidatų su šia būsena</h3>
                <p>Pabandykite pasirinkti kitą filtrą.</p>
              </div>
            )}
          </>
        )}

        {deleteTarget && (
          <ConfirmDialog
            message={`Ar tikrai norite pašalinti ${deleteTarget.first_name} ${deleteTarget.last_name} iš šio darbo skelbimo kandidatų?`}
            confirmText="Pašalinti"
            cancelText="Atšaukti"
            onConfirm={handleDeleteApplication}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </div>
  );
};

export default JobApplicationsPage;
